import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  resendVerificationEmail as apiResendVerification,
  verifyEmail as apiVerifyEmail,
} from '../api/auth.ts';
import { Skeleton } from '../components/Skeleton.tsx';
import { localizeApiError } from '../api/errorMessages.ts';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import { Button, FormField, Input, buttonClass } from '../ui/index.ts';
import { AUTH_LINK_CLASS, AuthPageFrame } from './auth/AuthPageFrame.tsx';

/**
 * Email verification landing — Field Notes rebuild (epic #950 PR-2,
 * formerly the console-shared `createVerifyEmail` factory
 * instantiation).
 *
 * Wire behavior preserved: auto-fires `POST /v1/auth/verify-email` from
 * `?token=` on mount (the POST mints a session cookie; `refresh()` then
 * hydrates the session context). Pending renders a Skeleton block;
 * missing-token and error (+ inline resend form) states are preserved.
 * Errors render inline only.
 *
 * Success renders a confirmation card rather than redirecting silently.
 * The redirect landed the user on a dashboard that looks identical to
 * an ordinary sign-in, so nothing on screen ever told them the
 * verification they just clicked through had actually worked. Matches
 * `console-web`.
 */

type VerifyState =
  | { readonly kind: 'missing-token' }
  | { readonly kind: 'pending' }
  | { readonly kind: 'success' }
  | { readonly kind: 'error'; readonly message: string };

export function VerifyEmail(): JSX.Element {
  const [search] = useSearchParams();
  const token = search.get('token');
  const { fetchImpl, refresh } = useSession();
  const t = useT();
  const [state, setState] = useState<VerifyState>(
    token === null || token.length === 0 ? { kind: 'missing-token' } : { kind: 'pending' },
  );
  const [resendEmail, setResendEmail] = useState('');
  const [resendSubmitting, setResendSubmitting] = useState(false);
  const [resendAck, setResendAck] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);

  useEffect(() => {
    if (token === null || token.length === 0) {
      return;
    }

    let cancelled = false;
    setState({ kind: 'pending' });

    (async () => {
      try {
        await apiVerifyEmail(token, fetchImpl);
        if (cancelled) {
          return;
        }
        await refresh();
        if (cancelled) {
          return;
        }
        setState({ kind: 'success' });
      } catch (err) {
        if (cancelled) {
          return;
        }
        setState({ kind: 'error', message: localizeApiError(err, t) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, fetchImpl, refresh]);

  async function handleResend(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const trimmed = resendEmail.trim();
    if (trimmed.length === 0) {
      return;
    }
    setResendError(null);
    setResendSubmitting(true);
    try {
      await apiResendVerification({ email: trimmed }, fetchImpl);
      setResendAck(true);
    } catch (err) {
      setResendError(localizeApiError(err, t));
    } finally {
      setResendSubmitting(false);
    }
  }

  return (
    <AuthPageFrame
      title={t('verify.subtitle')}
      // The description promises a button that only the success card
      // has. On the error and missing-token screens it would sit
      // directly above a red failure box and a resend form, telling the
      // reader to press something that is not there.
      description={state.kind === 'pending' ? t('verify.description') : undefined}
      width="lg"
    >
      {state.kind === 'missing-token' ? (
        <p className="m-0 text-body text-bad" role="alert" data-testid="verify-missing-token">
          {t('verify.missing_token')}
        </p>
      ) : null}

      {state.kind === 'pending' ? (
        <div className="flex flex-col gap-2.5" role="status" data-testid="verify-pending">
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-9 w-40" />
          <span className="sr-only">{t('verify.pending')}</span>
        </div>
      ) : null}

      {state.kind === 'success' ? (
        <div
          className="flex flex-col gap-3 rounded-lg border border-ok-line bg-raised p-5"
          data-testid="verify-success"
          role="status"
        >
          <h2 className="m-0 text-heading text-ink">{t('verify.success.title')}</h2>
          <p className="m-0 text-body text-ink-mid">{t('verify.success.body')}</p>
          <Link
            to="/dashboard"
            data-testid="verify-success-cta"
            className={`${buttonClass('primary')} mt-1 w-fit`}
          >
            {t('verify.success.cta')}
          </Link>
        </div>
      ) : null}

      {state.kind === 'error' ? (
        <div className="flex flex-col gap-4">
          <div
            className="flex flex-col gap-2 rounded-lg border border-bad-line bg-bad-tint p-5"
            data-testid="verify-error"
            role="alert"
          >
            <h2 className="m-0 text-heading text-bad">{t('verify.error.title')}</h2>
            <p className="m-0 text-body text-bad">{state.message}</p>
          </div>

          {resendAck ? (
            <div
              className="flex flex-col gap-2 rounded-lg border border-ok-line bg-raised p-5"
              data-testid="verify-resend-ack"
              role="status"
            >
              <h2 className="m-0 text-heading text-ink">{t('verify.resend.ack.title')}</h2>
              <p className="m-0 text-body text-ink-mid">{t('verify.resend.ack.body')}</p>
            </div>
          ) : (
            <form
              className="flex flex-col gap-4 rounded-lg border border-line-soft bg-raised p-5"
              onSubmit={handleResend}
              data-testid="verify-resend-form"
            >
              <FormField label={t('verify.resend.label')} fieldId="verify-resend-email">
                <Input
                  id="verify-resend-email"
                  type="email"
                  mono
                  autoComplete="email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.currentTarget.value)}
                  disabled={resendSubmitting}
                  required
                  data-testid="verify-resend-email"
                />
              </FormField>
              {resendError !== null ? (
                <p
                  className="m-0 text-label text-bad"
                  role="alert"
                  data-testid="verify-resend-error"
                >
                  {resendError}
                </p>
              ) : null}
              <Button
                type="submit"
                variant="primary"
                loading={resendSubmitting}
                disabled={resendEmail.trim().length === 0}
                data-testid="verify-resend-submit"
                className="w-fit"
              >
                {resendSubmitting ? t('verify.resend.submitting') : t('verify.resend.submit')}
              </Button>
            </form>
          )}

          <Link to="/login" className={AUTH_LINK_CLASS}>
            {t('common.back_to_login')}
          </Link>
        </div>
      ) : null}
    </AuthPageFrame>
  );
}
